import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiWifiOff, FiPause, FiPlay, FiAlertTriangle, FiCheck, FiLoader, FiChevronDown, FiChevronRight, FiCpu } from 'react-icons/fi';
import { getFabricCapacities, pauseFabricCapacity, resumeFabricCapacity, getFabricCapacityWorkloads, getFabricWorkspaces, getFabricWorkspaceItems, type FabricCapacity, type FabricCapacitiesResponse, type FabricWorkload, type FabricWorkspace, type FabricItem } from '../../api/fabric';
import { getConnections, type ConnectionService } from '../../api/connections';
import { getProjects } from '../../api/projects';
import { getArchitecturesByProject } from '../../api/architectures';

const SKU_CUS: Record<string, number | string> = {
  F2: 2, F4: 4, F8: 8, F16: 16, F32: 32, F64: 64,
  F128: 128, F256: 256, F512: 512, F1024: 1024,
  FT1: 'Trial', FTL64: 'Trial',
  PP3: 'PPU', PP2: 'PPU', PP1: 'PPU',
};

const STATE_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  Active:     { label: 'Active',      color: '#065F46', bg: '#D1FAE5', icon: <FiCheck size={12} /> },
  Paused:     { label: 'Suspendue',   color: '#92400E', bg: '#FEF3C7', icon: <FiPause size={12} /> },
  Suspended:  { label: 'Suspendue',   color: '#92400E', bg: '#FEF3C7', icon: <FiPause size={12} /> },
  Scaling:    { label: 'Scaling…',    color: '#1E40AF', bg: '#DBEAFE', icon: <FiLoader size={12} /> },
  Resuming:   { label: 'Reprise…',    color: '#065F46', bg: '#D1FAE5', icon: <FiLoader size={12} /> },
  Suspending: { label: 'Suspension…', color: '#92400E', bg: '#FEF3C7', icon: <FiLoader size={12} /> },
  Failed:     { label: 'Erreur',      color: '#991B1B', bg: '#FEE2E2', icon: <FiAlertTriangle size={12} /> },
  Invalid:    { label: 'Invalide',    color: '#6B7280', bg: '#F3F4F6', icon: <FiWifiOff size={12} /> },
};

const WORKLOAD_LABELS: Record<string, string> = {
  Kusto:         'KQL / Eventhouse',
  Gen2:          'Dataflow Gen2',
  Notebook:      'Spark Notebook',
  MiniWarehouse: 'Warehouse',
  Interactive:   'Requêtes interactives',
  Pipeline:      'Data Pipeline',
  Autotune:      'Auto-tuning',
  AI:            'IA / ML',
  SparkCore:     'Spark Core',
  ExtendedCore:  'Extended Core',
};

interface CanvasCapacity {
  name: string;
  sku: string;
  architectureName: string;
}

const WorkloadBar: React.FC<{ workload: FabricWorkload }> = ({ workload }) => {
  const pct = workload.maxMemoryPercent;
  const label = WORKLOAD_LABELS[workload.name] || workload.name;
  const isEnabled = workload.state === 'Enabled';
  const barColor = pct >= 80 ? '#EF4444' : pct >= 50 ? '#F59E0B' : '#14B8A6';

  return (
    <div className="flex items-center gap-3 py-1">
      <span className="text-xs text-gray-500 w-40 flex-shrink-0 truncate">{label}</span>
      {isEnabled ? (
        <>
          <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 rounded-full transition-all"
              style={{ width: `${pct}%`, backgroundColor: barColor }}
            />
          </div>
          <span className="text-xs font-semibold w-10 text-right" style={{ color: barColor }}>
            {pct}%
          </span>
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 flex-shrink-0">
            On
          </span>
        </>
      ) : (
        <>
          <div className="flex-1 bg-gray-50 rounded-full h-2" />
          <span className="text-xs text-gray-400 w-10 text-right">—</span>
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-400 border border-gray-200 flex-shrink-0">
            Off
          </span>
        </>
      )}
    </div>
  );
};

const ITEM_TYPE_ICON: Record<string, string> = {
  Lakehouse: '🏠', Notebook: '📓', Pipeline: '🔄', Dataset: '📊',
  Dataflow: '🌊', Report: '📈', SemanticModel: '🧠', Warehouse: '🏭',
  Eventhouse: '⚡', Eventstream: '📡', MLModel: '🤖', Dashboard: '📋',
  Datamart: '🗄️', KQLDatabase: '🔍', KQLQueryset: '🔎', SparkJobDefinition: '⚙️',
};

const WorkspaceItemsRow: React.FC<{ workspace: FabricWorkspace; connectionId: string }> = ({ workspace, connectionId }) => {
  const [items, setItems] = useState<FabricItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const toggle = async () => {
    if (!expanded && items.length === 0) {
      setLoading(true);
      try {
        const res = await getFabricWorkspaceItems(workspace.id, connectionId);
        setItems(res.items);
      } catch { /* non bloquant */ }
      setLoading(false);
    }
    setExpanded(e => !e);
  };

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center gap-3 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition text-left"
      >
        {expanded ? <FiChevronDown size={12} className="text-gray-400 flex-shrink-0" /> : <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />}
        <span className="text-sm font-medium text-gray-800 flex-1 truncate">{workspace.displayName}</span>
        <span className="text-xs text-gray-400 bg-white px-2 py-0.5 rounded border border-gray-200">{workspace.type}</span>
        {loading && <FiLoader size={12} className="animate-spin text-gray-400" />}
        {!loading && expanded && <span className="text-xs text-gray-400">{items.length} item{items.length > 1 ? 's' : ''}</span>}
      </button>

      {expanded && (
        <div className="divide-y divide-gray-50">
          {items.length === 0 && !loading && (
            <p className="px-8 py-2 text-xs text-gray-400 italic">Aucun item dans ce workspace</p>
          )}
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-3 px-8 py-2">
              <span className="text-base">{ITEM_TYPE_ICON[item.type] || '📦'}</span>
              <span className="text-sm text-gray-800 flex-1 truncate">{item.displayName}</span>
              <span className="text-xs text-gray-400">{item.type}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CapacityWorkloadPanel: React.FC<{ capacityId: string; connectionId: string; isStandardSku: boolean }> = ({ capacityId, connectionId, isStandardSku }) => {
  const [workloads, setWorkloads] = useState<FabricWorkload[]>([]);
  const [workspaces, setWorkspaces] = useState<FabricWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const promises: Promise<any>[] = [
      getFabricWorkspaces(connectionId).then(r => setWorkspaces(r.workspaces.filter(ws => ws.capacityId?.toLowerCase() === capacityId.toLowerCase()))).catch(() => {}),
    ];
    if (isStandardSku) {
      promises.push(
        getFabricCapacityWorkloads(capacityId, connectionId).then(r => setWorkloads(r.workloads)).catch(() => {})
      );
    }
    Promise.all(promises).finally(() => setLoading(false));
  }, [capacityId, connectionId, isStandardSku]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 ml-6 text-xs text-gray-400">
        <FiLoader size={12} className="animate-spin" />
        Chargement…
      </div>
    );
  }

  return (
    <div className="border-t border-gray-100 pt-4 pb-2 px-6 space-y-4">
      {/* Workspaces + items */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Workspaces ({workspaces.length})
        </p>
        {workspaces.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Aucun workspace visible pour ce SP sur cette capacité</p>
        ) : (
          <div className="space-y-1">
            {workspaces.map(ws => (
              <WorkspaceItemsRow key={ws.id} workspace={ws} connectionId={connectionId} />
            ))}
          </div>
        )}
      </div>

      {/* Workloads CU (F/P SKU uniquement) */}
      {isStandardSku && workloads.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Allocation CU ({workloads.filter(w => w.state === 'Enabled').length} workloads actifs)
          </p>
          <div className="space-y-0.5">
            {workloads.filter(w => w.state === 'Enabled').map(w => <WorkloadBar key={w.name} workload={w} />)}
          </div>
        </div>
      )}
    </div>
  );
};

const FabricMonitorTab: React.FC = () => {
  const [data, setData] = useState<FabricCapacitiesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<{ id: string; msg: string; ok: boolean } | null>(null);
  const [fabricConnections, setFabricConnections] = useState<ConnectionService[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string>('');
  const [canvasCapacities, setCanvasCapacities] = useState<CanvasCapacity[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [expandedWorkloads, setExpandedWorkloads] = useState<Set<string>>(new Set());

  useEffect(() => {
    getConnections()
      .then(conns => {
        const fab = conns.filter(c => c.provider === 'fabric' && c.is_active);
        setFabricConnections(fab);
        if (fab.length > 0) setSelectedConnection(fab[0].id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getProjects()
      .then(async (projects: any[]) => {
        const caps: CanvasCapacity[] = [];
        for (const proj of projects) {
          try {
            const architectures = await getArchitecturesByProject(proj.id);
            for (const arch of architectures) {
              const nodes = (arch.diagram_data as any)?.nodes || [];
              nodes.forEach((n: any) => {
                const t = n.data?.type || n.data?.resourceType || '';
                if (t === 'fabric_capacity') {
                  caps.push({
                    name: n.data?.label || n.data?.params?.display_name || n.data?.params?.name || 'Unnamed',
                    sku: n.data?.params?.sku || 'F2',
                    architectureName: arch.name || proj.name,
                  });
                }
              });
            }
          } catch { /* non bloquant */ }
        }
        setCanvasCapacities(caps);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!selectedConnection) return;
    try {
      setLoading(true);
      setError(null);
      const result = await getFabricCapacities(selectedConnection);
      setData(result);
      setLastRefresh(new Date());
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Erreur de chargement';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [selectedConnection]);

  useEffect(() => {
    if (selectedConnection) load();
  }, [selectedConnection, load]);

  useEffect(() => {
    if (!selectedConnection) return;
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [selectedConnection, load]);

  const handleAction = async (cap: FabricCapacity, action: 'pause' | 'resume') => {
    setActionLoading(cap.id);
    setActionMsg(null);
    try {
      const fn = action === 'pause' ? pauseFabricCapacity : resumeFabricCapacity;
      const result = await fn(cap.id, selectedConnection);
      setActionMsg({ id: cap.id, msg: result.message, ok: true });
      setTimeout(load, 3000);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Action échouée';
      setActionMsg({ id: cap.id, msg, ok: false });
    } finally {
      setActionLoading(null);
    }
  };

  const toggleWorkloads = (capId: string) => {
    setExpandedWorkloads(prev => {
      const next = new Set(prev);
      if (next.has(capId)) next.delete(capId);
      else next.add(capId);
      return next;
    });
  };

  const findCanvasMatch = (liveName: string): CanvasCapacity | undefined =>
    canvasCapacities.find(c =>
      c.name.toLowerCase() === liveName.toLowerCase() ||
      liveName.toLowerCase().includes(c.name.toLowerCase())
    );

  const unmatchedCanvas = canvasCapacities.filter(c =>
    !data?.capacities.some(live =>
      live.displayName.toLowerCase().includes(c.name.toLowerCase()) ||
      c.name.toLowerCase() === live.displayName.toLowerCase()
    )
  );

  if (fabricConnections.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <FiWifiOff size={48} className="text-gray-300 mb-4" />
        <p className="text-gray-600 font-medium mb-2">Aucune connexion Fabric active</p>
        <p className="text-gray-400 text-sm">
          Allez dans <strong>Paramètres → Connexions</strong> pour configurer une connexion Microsoft Fabric.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {fabricConnections.length > 1 && (
            <select
              value={selectedConnection}
              onChange={e => setSelectedConnection(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
            >
              {fabricConnections.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          {data && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-400">
                Via <strong>{data.connectionName}</strong>
                {lastRefresh && ` · Mis à jour ${lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
              </span>
              {data.isAdminAccess
                ? <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded-full">Admin — tous les workspaces</span>
                : <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 border border-gray-200 rounded-full" title="Accès membre — seules les capacités et workspaces dont le SP est membre sont visibles. Activez l'accès administrateur Fabric pour une vue complète.">Accès membre</span>
              }
            </div>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
        >
          <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <FiRefreshCw size={24} className="animate-spin text-teal-500 mr-3" />
          <span className="text-gray-500">Interrogation de l'API Fabric...</span>
        </div>
      )}

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {/* Capacités live */}
            <div style={{ background: 'linear-gradient(135deg, #0f766e 0%, #14B8A6 100%)', borderRadius: '1rem', padding: '1.5rem', color: 'white', boxShadow: '0 4px 16px rgba(20,184,166,0.3)', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Capacités live</span>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem' }}><FiCpu size={16} color="white" /></div>
              </div>
              <div style={{ fontSize: '2.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>{data.capacities.length}</div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>{data.capacities.filter(c => c.state === 'Active').length} actives en ce moment</div>
            </div>

            {/* Workspaces total */}
            <div style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #8B5CF6 100%)', borderRadius: '1rem', padding: '1.5rem', color: 'white', boxShadow: '0 4px 16px rgba(139,92,246,0.3)', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Workspaces total</span>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem', fontSize: '1rem' }}>🗂️</div>
              </div>
              {data.workspaceDataUnavailable ? (
                <>
                  <div style={{ fontSize: '2.25rem', fontWeight: 800, marginBottom: '0.25rem', color: 'rgba(255,255,255,0.4)' }}
                     title="L'API Admin Fabric retourne une erreur 500 pour les capacités Trial (FTL). Activez 'Autoriser les principaux de service à utiliser les API Power BI' dans le portail admin Power BI (app.powerbi.com/admin) pour débloquer le comptage via l'API PBI.">N/D</div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>Limitation API Trial</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '2.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>{data.workspacesTotal}</div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>Tous workspaces confondus</div>
                </>
              )}
            </div>

            {/* Capacités déployées */}
            {(() => {
              const matched = canvasCapacities.length - unmatchedCanvas.length;
              const allMatched = unmatchedCanvas.length === 0 && canvasCapacities.length > 0;
              const gradient = allMatched
                ? 'linear-gradient(135deg, #065F46 0%, #10B981 100%)'
                : canvasCapacities.length > 0
                  ? 'linear-gradient(135deg, #92400E 0%, #F59E0B 100%)'
                  : 'linear-gradient(135deg, #1e40af 0%, #3B82F6 100%)';
              const shadow = allMatched ? 'rgba(16,185,129,0.3)' : canvasCapacities.length > 0 ? 'rgba(245,158,11,0.3)' : 'rgba(59,130,246,0.3)';
              return (
                <div style={{ background: gradient, borderRadius: '1rem', padding: '1.5rem', color: 'white', boxShadow: `0 4px 16px ${shadow}`, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Capacités déployées</span>
                    <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem', fontSize: '1rem' }}>⚡</div>
                  </div>
                  {canvasCapacities.length > 0 ? (
                    <>
                      <div style={{ fontSize: '2.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>{matched} / {canvasCapacities.length}</div>
                      <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>canvas matchées en live · {data.capacities.length} live</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '2.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>{data.capacities.length}</div>
                      <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>live · aucune sur canvas</div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Capacités live */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Capacités Fabric — État live</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {data.capacities.map(cap => {
                const stateConf = STATE_CONFIG[cap.state] || STATE_CONFIG['Invalid'];
                const cus = cap.sku in SKU_CUS ? SKU_CUS[cap.sku] : '?';
                const canvasMatch = findCanvasMatch(cap.displayName);
                const isActioning = actionLoading === cap.id;
                const msg = actionMsg?.id === cap.id ? actionMsg : null;
                const isExpanded = expandedWorkloads.has(cap.id);
                const isStandardSku = typeof cus === 'number';
                const canExpand = cap.state === 'Active' || cap.state === 'Scaling';

                return (
                  <div key={cap.id} className="px-6 py-4">
                    <div className="flex items-center gap-4 flex-wrap">
                      <img src="/icons/fabric/Microsoft-Fabric.svg" alt="" className="w-8 h-8 flex-shrink-0"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900">{cap.displayName}</span>
                          {canvasMatch && (
                            <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                              Canvas: {canvasMatch.architectureName}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {cap.sku} · {typeof cus === 'number' ? `${cus} CUs` : cus} · {cap.region}
                        </div>
                        {msg && (
                          <div className={`text-xs mt-1 ${msg.ok ? 'text-green-600' : 'text-red-600'}`}>
                            {msg.msg}
                          </div>
                        )}
                      </div>

                      <div className="text-center min-w-16">
                        <div className="text-lg font-bold text-gray-900">
                          {cap.workspaceCount === null ? <span className="text-gray-300 text-base">N/D</span> : cap.workspaceCount}
                        </div>
                        <div className="text-xs text-gray-400">workspaces</div>
                      </div>

                      <div
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                        style={{ background: stateConf.bg, color: stateConf.color }}
                      >
                        {stateConf.icon}
                        {stateConf.label}
                      </div>

                      {canExpand && (
                        <button
                          onClick={() => toggleWorkloads(cap.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <FiCpu size={12} />
                          Workloads
                          {isExpanded ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                        </button>
                      )}

                      <div className="flex gap-2">
                        {(cap.state === 'Active' || cap.state === 'Scaling') && (
                          <button
                            onClick={() => handleAction(cap, 'pause')}
                            disabled={isActioning}
                            title="Suspendre la capacité"
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-50 transition-colors"
                          >
                            {isActioning ? <FiLoader size={12} className="animate-spin" /> : <FiPause size={12} />}
                            Suspendre
                          </button>
                        )}
                        {(cap.state === 'Paused' || cap.state === 'Suspended') && (
                          <button
                            onClick={() => handleAction(cap, 'resume')}
                            disabled={isActioning}
                            title="Reprendre la capacité"
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
                          >
                            {isActioning ? <FiLoader size={12} className="animate-spin" /> : <FiPlay size={12} />}
                            Reprendre
                          </button>
                        )}
                      </div>
                    </div>

                    {isExpanded && canExpand && (
                      <CapacityWorkloadPanel capacityId={cap.id} connectionId={selectedConnection} isStandardSku={isStandardSku} />
                    )}
                  </div>
                );
              })}

              {data.capacities.length === 0 && (
                <div className="px-6 py-10 text-center text-gray-400 text-sm">
                  Aucune capacité Fabric accessible avec ce Service Principal.
                </div>
              )}
            </div>
          </div>

          {unmatchedCanvas.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <FiAlertTriangle size={16} className="text-amber-600" />
                <span className="font-medium text-amber-800 text-sm">
                  {unmatchedCanvas.length} capacité{unmatchedCanvas.length > 1 ? 's' : ''} canvas sans déploiement détecté
                </span>
              </div>
              <div className="space-y-1">
                {unmatchedCanvas.map((c, i) => (
                  <div key={i} className="text-xs text-amber-700 flex items-center gap-2">
                    <span className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">{c.sku}</span>
                    <span>{c.name}</span>
                    <span className="text-amber-500">— Architecture: {c.architectureName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FabricMonitorTab;
